import { ol_Feature } from "@omnia/exports/openlayers"
import { datalayers, defineComponent, fields, FieldViewModel, IDatalayerMapDependencies, IOmniaShell, SharedFieldsService } from "../omnia"
import { IDataLayer, ISoilService, LayerZone } from "../types"
import { createDashboardContext } from "./context"
import { SoilMapContext } from "./map/context"
import { HsaLayer } from "./map/view/hsa"
import { IDashboard, IDashboardContext } from "./types"
import { getLayerZoneId } from "./utils"

export class DashboardCtrl implements IDashboard {

    static create() {
        return defineComponent(DashboardCtrl, "app/soil/dashboard/dashboard.html")
    }

    public readonly readOnly = this.shell.isReadOnly(p => p.soil)

    public map: SoilMapContext
    private mapSelectionWatcher: Function

    private _layers: IDataLayer[]

    public context: IDashboardContext

    private _fields: FieldViewModel[]

    private _selectedField: FieldViewModel
    private _selectedCropping: FieldViewModel
    private _selectedZone: LayerZone


    static $inject = [
        "$scope",
        "$timeout",
        "OmniaShell",
        "OmniaSoilService",
        "OmniaFieldsService",
        "OmniaDatalayersDependencies"
    ]

    constructor(
        private $scope: ng.IScope,
        private $timeout: ng.ITimeoutService,
        private shell: IOmniaShell,
        private svc: ISoilService,
        private fieldSvc: SharedFieldsService,
        private mapDependencies: IDatalayerMapDependencies) {

    }

    $onInit() {

        this.refresh().then(() => {

            fields.watchFields(this.fieldSvc, this.$scope, f => {
                if (this.svc.ready) {
                    this.refreshFields(f)
                }
            })

        })

        this.$scope.$watch("vm.svc.stateVersion", (a, b) => {

            if (a !== b) {
                return this.refreshInternal()
            }

        })

    }

    $onDestroy() {
        this.dispose()
    }

    dispose() {

        if (this.mapSelectionWatcher) {
            this.mapSelectionWatcher()
            this.mapSelectionWatcher = null
        }

    }


    public refresh() {

        return this.svc.refresh().then(() => this.refreshInternal())

    }

    refreshInternal() {

        return this.svc.getSoilLayers(true, null).then(layers => {

            this._fields = this.fieldSvc.fields || []
            this._layers = layers || []

            if (!this.map) {

                this.dispose()

                this.map = new SoilMapContext(this.svc, layers, this.mapDependencies)
                this.mapSelectionWatcher = this.map.watchSelection(features => this.updateSelectionFromMap(features))

            }
            else {

                this.map.refreshViews(layers)

            }

            const context = createDashboardContext(this, this.context)

            if (this._selectedField || this._selectedCropping || this._selectedZone) {
                context.selectedField = this.selectedField
                context.selectedCropping = this._selectedCropping
                context.selectedZone = this.selectedZone
            }

            this.context = context

            this.refreshMapSelection()

        })

    }

    refreshFields(fields: FieldViewModel[]) {

        this._fields = fields || []

        if (this._selectedField && (this._fields.indexOf(this._selectedField) < 0)) {
            this.selectedField = null
        }

        if (this.map) {
            this.map.refreshCurrentView()
            this.map.viewDefaultExtent()
        }

    }

    get fields() {
        return this._fields
    }

    get layers() {
        return this._layers
    }

    showSsmCard() {
        return this.svc.hasSsmAnalyses()
    }

    get selectedField() {
        return this._selectedField
    }

    set selectedField(value: FieldViewModel) {
        this.setSelectedField(value, null, true)
    }

    get selectedZone(): LayerZone {
        return this._selectedZone
    }

    public setSelectedField(item: FieldViewModel, zone: LayerZone, updateMap: boolean) {

        let cropping = item && item.isCropping() ? item : null
        let field = cropping ? cropping.fieldVm : item

        let hsaId: number

        if (zone && (zone.layer instanceof HsaLayer)) {
            let data = zone.zone.Data
            hsaId = data ? data["HealthySoilsAssessmentId"] : null
        }

        this._selectedField = field
        this._selectedCropping = cropping
        this._selectedZone = zone

        const context = createDashboardContext(this, null)

        context.selectedField = this.selectedField
        context.selectedCropping = this._selectedCropping
        context.selectedZone = this.selectedZone
        context.selectedHsaId = hsaId

        this.context = context

        if (updateMap && this.map) {
            this.map.updateMapSelection(zone || item)
            this.map.zoom(item)
        }

    }

    private setSelectedZone(zone: LayerZone) {

        let layer = zone.layer
        let field = this.svc.findCropping(layer.croppingId) || this.svc.findField(layer.fieldId)

        this.setSelectedField(field, zone, false)

    }

    private updateSelectionFromMap(features: ol_Feature[]) {

        if (features && features.length) {

            let data = features[0].get("data")

            this.$timeout().then(() => {

                if (fields.isFieldViewModel(data)) {
                    this.setSelectedField(data, null, false)
                }
                else if (data instanceof datalayers.DatalayerZone) {
                    this.setSelectedZone(data)
                }

            })

            return true

        }

    }

    refreshMapSelection() {

        const view = this.map.view
        const zone = this._selectedZone

        let selection = [
            getLayerZoneId(zone),
            this._selectedCropping,
            this._selectedField
        ].filter(i => !!i)

        if (view) {

            let found = false

            selection.forEach(i => {

                let match = view.findFeature(i)

                let feature = match && match.length ? match[0] : null

                if (feature) {
                    this.map.updateMapSelection(feature)
                    found = true
                    return true
                }


            })

            if (!found) {
                this.map.updateMapSelection(null)
            }

        }
    }

    private editMapAllocations() {

        this.shell.showComponentAsDialog("omnia-soil-global-map-popup", null,
            {
                backdrop: "static",
                size: "full-screen"
            }).then(r => this.refresh())

    }

    showDialog(component: string, args: any, optionsOverride?: any) {

        let options = optionsOverride ? optionsOverride :
            {
                backdrop: "static",
                size: "lg"
            }

        let result = this.shell.showComponentAsDialog(component, args, options)

        if (result) {

            result = result.then(ok => {

                if (ok) {
                    this.refresh()
                }

                return ok

            })

        }

        return result

    }

}
